import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Tooltip, message } from "antd";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Axios from "axios";
import { connect } from "react-redux";
import { APIRequest, SignupUser } from "../../Utils/APIManager";
import { onLanguageChange } from "../../Redux/Actions/LanguageActions";
import backgroundImage from "../../assets/background_logout.jpg";
import {
  STR_Tam_AlreadyHaveAccount,
  STR_AlreadyHaveAccount,
  STR_Email,
  STR_Tam_Email,
  STR_Login,
  STR_Tam_Login,
  STR_Tam_Password,
  STR_Signup,
  STR_Tam_Signup,
  STR_SignupContentEnglish,
  STR_SignupContentTamil,
  STR_PasswordEmpty,
  STR_Tam_PasswordEmpty,
  STR_Confirm_PasswordEmpty,
  STR_Tam_Confirm_PasswordEmpty,
  STR_EmailEmpty,
  STR_Tam_EmailEmpty,
  STR_EmailValid,
  STR_Tam_EmailValid,
  STR_PasswordValidation,
  STR_TAM_PasswordValidation,
  STR_Tam_SamePasswordValid,
  STR_SamePasswordValid,
  STR_FullName,
  STR_Tam_FullName,
  STR_FullName_Empty,
  STR_Tam_FullName_Empty,
  STR_Tam_FullName_Pattern,
  STR_FullName_Pattern,
  STR_Tam_EDIT_MSG,
  STR_EDIT_MSG,
  STR_TAM_Password_Requirement,
  STR_Password_Requirement,
  STR_Tam_NewPasswordEmpty,
  STR_NewPasswordEmpty,
} from "../../Config.js/Strings";

const SignupModal = ({ open, onClose, LanguageReducer, onLanguageChange }) => {
  const { isTamil } = LanguageReducer;
  const [form] = Form.useForm();
  const [tamilWord, setTamilWord] = useState([]);
  const [selectedTamilWord, setSelectedTamilWord] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Handle Tamil/English switch based on localStorage
  useEffect(() => {
    const isTamilLang = localStorage.getItem("agarathi_isTamil");
    onLanguageChange(isTamilLang !== "false");
  }, [onLanguageChange]);

  // Google Input Tools API for Tamil transliteration
  const handleTranslate = async (name) => {
    try {
      const url = `https://inputtools.google.com/request?text=${name}&itc=ta-t-i0-und&num=13&ie=utf-8&oe=utf-8&app=demopage`;
      const response = await Axios.get(url);
      const translatedText = response.data[1][0][1];
      return translatedText;
    } catch (error) {
      console.error("Translation error:", error);
      return [];
    }
  };

  const handleFullNameChange = async (e) => {
    const value = e.target.value;
    if (value.length > 0) {
      const translated = await handleTranslate(value);
      setTamilWord(translated || []);
    } else {
      setTamilWord([]);
      setSelectedTamilWord("");
    }
  };

  const handleItemClick = (item) => {
    setSelectedTamilWord(item);
    setTamilWord([]);
  };

  // ✅ UPDATED: Handle Signup with OTP trigger
  const handleSignup = async (values) => {
    const payload = {
      email: values.email,
      full_name: values.full_name,
      full_name_tamil: selectedTamilWord,
      password: values.password,
      conform_password: values.conform_password,
      signup_type: "NORMAL",
      language: isTamil ? "TAMIL" : "ENGLISH",
    };

    setIsLoading(true);
    try {
      const response = await APIRequest.getPostService(SignupUser, payload);
      if (response.status) {
        message.success(response.message);

        // Close signup modal
        onClose();

        // ✅ Trigger OTP verification popup (email + language)
        window.dispatchEvent(
          new CustomEvent("openOtpVerification", {
            detail: { email: values.email, isTamil },
          })
        );
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error(error);
      message.error("Signup failed!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={750}
      className="signup_full_popup"
      bodyStyle={{
        padding: 0,
        borderRadius: "20px",
        overflow: "hidden",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "530px",
        position: "relative",
      }}
      destroyOnClose
    >
      <div
        className="signup_overlay flex items-center justify-center w-full h-full"
        style={{ height: "100%", width: "100%" }}
      >
        <div
          className="signup_form_container"
          style={{
            width: "80%",
            maxWidth: "420px",
            height: "90%",
            overflowY: "auto",
            padding: "30px",
            borderRadius: "16px",
            marginLeft: "42%",
          }}
        >
          <h2 className="heading_text text-center text-xl font-semibold mb-4 text-white">
            {isTamil ? STR_SignupContentTamil : STR_SignupContentEnglish}
          </h2>

          <Form form={form} layout="vertical" onFinish={handleSignup}>
            {/* Full Name */}
            <Form.Item
              name="full_name"
              className="fullname-form-item"
              rules={[
                {
                  required: true,
                  message: isTamil
                    ? STR_Tam_FullName_Empty
                    : STR_FullName_Empty,
                },
                {
                  pattern: new RegExp(/^[a-zA-Z0-9 ]*$/),
                  message: isTamil
                    ? STR_Tam_FullName_Pattern
                    : STR_FullName_Pattern,
                },
              ]}
            >
              <Input
                placeholder={isTamil ? STR_Tam_FullName : STR_FullName}
                prefix={
                  <UserOutlined style={{ color: "#737575ff", width: "100%" }} />
                }
                onChange={handleFullNameChange}
                style={{
                  borderRadius: "12px",
                  height: "40px",
                  fontSize: "12px",
                }}
              />
            </Form.Item>

            {tamilWord.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {tamilWord.slice(0, 8).map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleItemClick(item)}
                    style={{
                      background: "#f0f0f0",
                      padding: "5px 8px",
                      borderRadius: "8px",
                      margin: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {item}
                  </div>
                ))}
                <p style={{ fontSize: "12px" }}>
                  <strong>{isTamil ? STR_Tam_EDIT_MSG : STR_EDIT_MSG}</strong>
                </p>
              </div>
            )}

            {selectedTamilWord && (
              <Input
                value={selectedTamilWord}
                prefix={
                  <UserOutlined style={{ color: "#737575ff", width: "100%" }} />
                }
                onChange={(e) => setSelectedTamilWord(e.target.value)}
                style={{
                  borderRadius: "12px",
                  height: "40px",
                  fontSize: "12px",
                  marginBottom: "10px",
                }}
              />
            )}

            {/* Email */}
            <Form.Item
              name="email"
              className="fullname-form-item"
              rules={[
                {
                  required: true,
                  message: isTamil ? STR_Tam_EmailEmpty : STR_EmailEmpty,
                },
                {
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: isTamil ? STR_Tam_EmailValid : STR_EmailValid,
                },
              ]}
            >
              <Input
                placeholder={isTamil ? STR_Tam_Email : STR_Email}
                prefix={
                  <MailOutlined style={{ color: "#737575ff", width: "100%" }} />
                }
                style={{
                  borderRadius: "12px",
                  height: "40px",
                  fontSize: "12px",
                }}
              />
            </Form.Item>

            {/* Password */}
            <Form.Item
              name="password"
              className="password_form"
              rules={[
                {
                  required: true,
                  message: isTamil ? STR_Tam_PasswordEmpty : STR_PasswordEmpty,
                },
                {
                  min: 8,
                  message: isTamil
                    ? STR_TAM_PasswordValidation
                    : STR_PasswordValidation,
                },
              ]}
            >
              <Input.Password
                className="custom-password"
                placeholder={isTamil ? STR_Tam_Password : "Password"}
                prefix={
                  <LockOutlined style={{ color: "#737575ff", width: "100%" }} />
                }
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                addonAfter={
                  <Tooltip
                    title={
                      <div>
                        <p>
                          1.{" "}
                          {isTamil
                            ? STR_Tam_NewPasswordEmpty
                            : STR_NewPasswordEmpty}
                        </p>
                        <p>
                          2.{" "}
                          {isTamil
                            ? STR_TAM_PasswordValidation
                            : STR_PasswordValidation}
                        </p>
                        <p>
                          3.{" "}
                          {isTamil
                            ? STR_TAM_Password_Requirement
                            : STR_Password_Requirement}
                        </p>
                      </div>
                    }
                  >
                    <InfoCircleOutlined
                      style={{ color: "#10add1", fontSize: "15px" }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>

            {/* Confirm Password */}
            <Form.Item
              name="conform_password"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: isTamil
                    ? STR_Tam_Confirm_PasswordEmpty
                    : STR_Confirm_PasswordEmpty,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        isTamil
                          ? STR_Tam_SamePasswordValid
                          : STR_SamePasswordValid
                      )
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={
                  isTamil
                    ? STR_Tam_Confirm_PasswordEmpty
                    : STR_Confirm_PasswordEmpty
                }
                prefix={
                  <LockOutlined style={{ color: "#737575ff", width: "100%" }} />
                }
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                style={{
                  borderRadius: "12px",
                  height: "40px",
                  fontSize: "12px",
                }}
              />
            </Form.Item>

            {/* Submit */}
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading}
              className="w-full mt-4"
              style={{
                backgroundColor: "#0FA6C9",
                borderRadius: "12px",
                height: "40px",
                width: "75%",
                fontWeight: "600",
                marginLeft: "50px",
              }}
            >
              {isTamil ? STR_Tam_Signup : STR_Signup}
            </Button>

            {/* Already have an account */}
            <div className="text-center mt-4">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "550",
                  marginLeft: "2rem",
                }}
              >
                {isTamil ? STR_Tam_AlreadyHaveAccount : STR_AlreadyHaveAccount}{" "}
                <span
                  style={{ color: "#35A6C9", cursor: "pointer" }}
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(new Event("openLoginPopup"));
                  }}
                >
                  {isTamil ? STR_Tam_Login : STR_Login}
                </span>
              </span>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  LanguageReducer: state.LanguageReducer,
});

export default connect(mapStateToProps, { onLanguageChange })(SignupModal);
